import React, { useEffect, useState } from 'react'
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatPrice,
} from '../../../../lib/utils/formatNumber'
import { HeroIcons } from '../../../../lib/utils/heroIcons'
import { buttonHierarchy, buttonShape } from '../../../Button'
import { useTranslation } from 'next-i18next'
import routes from '../../../../config/routes'
import { setCurrentProperty } from '../../../../store'
import useCurrency from '../../../../hooks/useCurrency'
import useLocale from '../../../../hooks/useLocale'
import ProgressBar from '../../ProgressBar'
import Countdown from '../../Countdown'
import { useDispatch } from 'react-redux'
import { OngoingOrder } from '../../../../types/Order/OngoingOrder'
import OrderService from '../../../../services/order.service'
import Modal from '../../../Modal'
import { DialogTitle } from '@headlessui/react'
import FractionInput from './FractionInput'
import { useRouter } from 'next/router'
import { getLocalePathFromPathname } from '../../../../lib/utils/language'
import { useAuth } from '../../../../providers/AuthProvider'
import ContributorsCount from './ContributorsCount'
import { GoogleTagManagerService } from '../../../../services/google-tag-manager.service'
import { CurrencyEnum } from '../../../../types/Currency'
import { mapContent, mapItem } from '../../../../lib/utils/helperGtm'
import { ClubEntry, ClubEntryStatus } from '../../../../types/club/ClubEntry'
import LinkButton from '../../../Button'
import { Property } from '../../../../types/Property/Property'

type MobileBuyCardProps = {
  minFractionAmount: number
  clubEntry?: ClubEntry
  property: Property
}

export default function MobileBuyCard({
  minFractionAmount,
  clubEntry,
  property,
}: MobileBuyCardProps) {
  const { t } = useTranslation('originals/property')
  const { currency } = useCurrency()
  const { locale } = useLocale()
  const router = useRouter()
  const dispatch = useDispatch()
  const { isLoggedIn } = useAuth()

  // const property = useSelector(currentProperty)

  const [cardOpen, setCardOpen] = useState<boolean>(false)
  const [shareLinkCopied, setShareLinkCopied] = useState<boolean>(false)
  const [ongoingOrder, setOngoingOrder] = useState<OngoingOrder>()
  const [proceedToPurchaseModalIsOpen, setProceedToPurchaseModalIsOpen] =
    useState<boolean>(false)
  const [fractionAmount, setFractionAmount] =
    useState<number>(minFractionAmount)
  const isDisabled =
    property?.fundingStatus !== 'funding' || fractionAmount === 0

  const handleActionOnCountdownComplete = (): void => {
    if (property) {
      dispatch(setCurrentProperty({ ...property, fundingStatus: 'funding' }))
    }
  }

  const handleOpenCloseCard = (): void => {
    setCardOpen(!cardOpen)
  }

  const handleValueChange = (value: number): void => {
    setFractionAmount(value)
  }

  const handleOpenProceedToPurchaseModalIsOpen = (e: any): void => {
    if (!property) return

    if (isDisabled) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    if (ongoingOrder) {
      if (ongoingOrder.stockLock && ongoingOrder.stockLock.lock) {
        router.replace({
          pathname: `${getLocalePathFromPathname(routes.checkout, locale)}`,
          query: { propertyId: property.id },
        })
      } else {
        router.replace({
          pathname: `${getLocalePathFromPathname(
            routes.checkoutQueue,
            locale,
          )}`,
          query: { propertyId: property.id },
        })
      }
    } else {
      setProceedToPurchaseModalIsOpen(true)
    }
  }

  const redirectToPurchase = (): void => {
    if (!property) return
    GoogleTagManagerService.AddToCart({
      value: formatPrice(property.fractionCurrentPrice * fractionAmount),
      currency: CurrencyEnum.EUR,
      contents: [mapContent(property, fractionAmount)],
      items: [mapItem(property, fractionAmount)],
    })
    router.replace({
      pathname: `${getLocalePathFromPathname(routes.checkoutQueue, locale)}`,
      query: {
        fractionAmount: fractionAmount,
        propertyId: property.id,
      },
    })
  }

  const fetchOrder = async (propertyId: string) => {
    try {
      const ongoingOrder: OngoingOrder =
        await OrderService.getOngoingOrder(propertyId)
      setOngoingOrder(ongoingOrder)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (!property) return
    if (isLoggedIn) {
      fetchOrder(property.id)
    } else {
      setOngoingOrder(undefined)
    }
  }, [locale, property, isLoggedIn])

  useEffect(() => {
    setFractionAmount(minFractionAmount)
  }, [minFractionAmount])

  const onShareLink = (): void => {
    navigator.clipboard.writeText(window.location.href)
    setShareLinkCopied(true)
    setTimeout(() => setShareLinkCopied(false), 2000)
  }

  if (!property) {
    //todo loading
    return <></>
  }

  const proceedToPurchaseModal = (
    <Modal
      isOpen={proceedToPurchaseModalIsOpen}
      onClose={() => setProceedToPurchaseModalIsOpen(false)}
    >
      <div className="p-3">
        <div className="pb-3">
          <div className="flex items-start">
            <div className="flex-grow">
              <DialogTitle
                as="h3"
                className="text-lg font-medium leading-6 text-gray-900"
              >
                {t('buyCard.mobileFractionSelect.title')}
              </DialogTitle>
            </div>

            <button onClick={() => setProceedToPurchaseModalIsOpen(false)}>
              <HeroIcons.XMarkIcon className="h-auto w-6" />
            </button>
          </div>
          <p className="text-sm text-gray-500">
            {t('buyCard.mobileFractionSelect.subtitle')}
          </p>

          <div className="mt-1">
            <FractionInput
              property={property}
              onValueChange={handleValueChange}
              minFractionAmount={minFractionAmount}
              initialValue={minFractionAmount}
            />
          </div>

          {clubEntry && clubEntry.status !== ClubEntryStatus.COMPLETED && (
            <div className="ml-0.5 mt-2">
              <p className="text-xs text-gray-600">
                {t('buyCard.clubdeal.minimumFraction')}
              </p>
              <p className="mt-1 text-xs italic text-gray-600">
                {t('buyCard.clubdeal.contactUs')}
              </p>
            </div>
          )}
        </div>
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 md:w-auto"
          onClick={redirectToPurchase}
        >
          {t('buyCard.mobileFractionSelect.proceedBtn')}
        </button>
      </div>
    </Modal>
  )

  return (
    <>
      {proceedToPurchaseModal}
      <div className="relative flex">
        <button
          className="border-1 absolute -top-4 left-1/2 z-10 flex h-6 w-10 -translate-x-1/2 transform items-center rounded-full border border-neutralGray/40 bg-neutralWhite shadow"
          onClick={handleOpenCloseCard}
        >
          {cardOpen ? (
            <HeroIcons.ChevronDownIcon
              className="mx-auto h-5 w-5 items-center text-neutralBlack"
              aria-hidden="true"
            />
          ) : (
            <HeroIcons.ChevronUpIcon
              className="mx-auto h-5 w-5 items-center text-neutralBlack"
              aria-hidden="true"
            />
          )}
        </button>

        <div className="border-1 standalone-mobilebuycard-padding-bottom w-full rounded-t-xl border-neutralGray/50 bg-neutralWhite px-3 pb-2 pt-3">
          <div
            className={`${
              cardOpen ? '"border-b pb-3" border-neutralGray/50' : ''
            }`}
          >
            <div className="flex items-center">
              <div className="min-w-[9em]">
                <div>
                  <div className="text-xs font-thin text-neutralBlack">
                    {t('buyCard.amountToCollect')}
                  </div>
                  <div
                    className={`font-medium text-neutralBlack ${
                      cardOpen ? 'text-3xl' : 'text-xl leading-5'
                    }`}
                  >
                    {formatCurrency(
                      property.fractionCount * property.fractionInitialPrice,
                      0,
                      2,
                      currency,
                      locale,
                    )}
                  </div>
                </div>

                <div className="pt-0.5 text-xs text-neutralBlack">
                  {formatNumber(property.fractionCount)}{' '}
                  {t('buyCard.fractionsAt')}{' '}
                  <strong>
                    {formatCurrency(
                      property.fractionInitialPrice,
                      0,
                      2,
                      currency,
                      locale,
                    )}
                  </strong>
                </div>
              </div>

              {cardOpen ? (
                <div className="ml-auto flex items-center">
                  <button onClick={onShareLink}>
                    <div className="flex items-center font-bold text-neutralBlack hover:text-brandLightBlue">
                      {shareLinkCopied ? (
                        <HeroIcons.CheckIcon
                          className="mr-1 h-5 w-5"
                          aria-hidden="true"
                        />
                      ) : (
                        <HeroIcons.ShareIcon
                          className="mr-1 h-5 w-5"
                          aria-hidden="true"
                        />
                      )}
                      {shareLinkCopied
                        ? t('topBar.shareLinkCopied')
                        : t('topBar.share')}
                    </div>
                  </button>
                </div>
              ) : (
                <div className="w-full">
                  {property.fundingStatus === 'funding' && (
                    <div>
                      <div className="pb-1">
                        <LinkButton
                          hierarchy={buttonHierarchy.primary}
                          shape={buttonShape.rectangle}
                          className="text-md h-full w-full px-2 py-1 font-medium"
                          disabled={isDisabled}
                          onClick={handleOpenProceedToPurchaseModalIsOpen}
                        >
                          {ongoingOrder
                            ? t('buyCard.continuePurchase')
                            : t('buyCard.proceed')}
                        </LinkButton>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-end">
                          <span className="flex-grow text-xs font-light text-neutralBlack">
                            <strong className="text-base font-bold">
                              {formatCurrency(
                                property.fractionFunded * 10 * 100,
                                0,
                                2,
                                currency,
                                locale,
                              )}
                            </strong>
                            {' / '}
                            {formatCurrency(
                              property.fractionCount * 10 * 100,
                              0,
                              2,
                              currency,
                              locale,
                            )}
                          </span>
                          {/*<ContributorsCount*/}
                          {/*  property={property}*/}
                          {/*  smallText={true}*/}
                          {/*/>*/}
                        </div>

                        <ProgressBar
                          fractionCount={property.fractionCount}
                          fractionFunded={property.fractionFunded}
                          id={property.id}
                          lineHeight={2}
                        />
                      </div>
                    </div>
                  )}

                  {property.fundingStatus === 'not_started' && (
                    <div className="space-y-2 pt-3">
                      <div className="text-center text-xs font-semibold text-neutralBlack">
                        {t('financingCountdownCard.financingAvailableIn')}
                      </div>
                      <Countdown
                        targetDate={property.fundingStartAt}
                        onCountdownComplete={handleActionOnCountdownComplete}
                        className="text-xs text-neutralBlack"
                      />
                    </div>
                  )}
                  {property.fundingStatus === 'reimbursed' && (
                    <div className="pt-3 text-xs font-semibold text-emerald-700">
                      {t('buyCard.reimbursed')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={`${cardOpen ? 'block' : 'hidden'}`}>
            <div className="border-b border-neutralGray/50 pb-3 pt-3">
              {property.revaluationPerYear > 0 ? (
                <div className="flex">
                  <div className="flex items-center">
                    <HeroIcons.ChartBarIcon
                      className="mr-1 h-3 w-auto text-neutralBlack"
                      aria-hidden="true"
                    />
                    <div className="text-xs font-thin text-neutralBlack">
                      {t('buyCard.propertyReassessment')}
                    </div>
                  </div>
                  <div className="flex flex-auto items-center justify-center px-2">
                    <div className="flex h-[1px] w-full border-t-[0.5px] border-dashed border-neutralGray/50 text-center"></div>
                  </div>
                  <div className="flex text-right">
                    <div className="ml-1 font-bold text-neutralBlack">
                      {formatPercentage(property.revaluationPerYear, 2, true)}
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="flex">
                <div className="flex items-center">
                  <HeroIcons.BanknotesIcon
                    className="mr-1 h-3 w-auto text-neutralBlack"
                    aria-hidden="true"
                  />
                  <div className="text-xs font-thin text-neutralBlack">
                    {t('buyCard.netRentalYields')}
                  </div>
                </div>
                <div className="flex flex-auto items-center justify-center px-2">
                  <div className="flex h-[1px] w-full border-t-[0.5px] border-dashed border-neutralGray/50 text-center"></div>
                </div>
                <div className="flex text-right">
                  <div className="ml-1 font-bold text-neutralBlack">
                    {formatPercentage(property.rentalYieldPerYear, 2, true)}
                  </div>
                </div>
              </div>

              {property.timeFrameMonths ? (
                <div className="flex">
                  <div className="flex items-center">
                    <HeroIcons.ClockIcon
                      className="mr-1 h-3 w-auto text-neutralBlack"
                      aria-hidden="true"
                    />
                    <div className="text-xs font-thin text-neutralBlack">
                      {t('buyCard.propertyDuration')}
                    </div>
                  </div>
                  <div className="flex flex-auto items-center justify-center px-2">
                    <div className="flex h-[1px] w-full border-t-[0.5px] border-dashed border-neutralGray/50 text-center"></div>
                  </div>
                  <div className="flex text-right">
                    <div className="ml-1 font-bold text-neutralBlack">
                      {t('buyCard.propertyDurationInMonth', {
                        months: property.timeFrameMonths,
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {property.fundingStatus === 'funding' && (
              <div className="flex items-center space-x-3 pt-3">
                <div className="flex-grow">
                  <div className="flex items-center">
                    <span className="flex-grow text-xs font-light text-neutralBlack">
                      <strong className="text-base font-bold">
                        {formatCurrency(
                          property.fractionFunded * 10 * 100,
                          0,
                          2,
                          currency,
                          locale,
                        )}
                      </strong>
                      {' / '}
                      {formatCurrency(
                        property.fractionCount * 10 * 100,
                        0,
                        2,
                        currency,
                        locale,
                      )}
                    </span>
                  </div>
                  <ProgressBar
                    fractionCount={property.fractionCount}
                    fractionFunded={property.fractionFunded}
                    id={property.id}
                    lineHeight={2}
                  />
                  <ContributorsCount property={property} smallText={true} />
                </div>
                <div>
                  <LinkButton
                    hierarchy={buttonHierarchy.primary}
                    shape={buttonShape.rectangle}
                    className="text-md h-full w-full px-2 py-1 font-medium"
                    disabled={isDisabled}
                    onClick={handleOpenProceedToPurchaseModalIsOpen}
                  >
                    {ongoingOrder
                      ? t('buyCard.continuePurchase')
                      : t('buyCard.proceed')}
                  </LinkButton>
                </div>
              </div>
            )}

            {property.fundingStatus === 'not_started' && (
              <div className="space-y-2 pt-3">
                <div className="text-center text-xs font-semibold text-neutralBlack">
                  {t('financingCountdownCard.financingAvailableIn')}
                </div>
                <Countdown
                  targetDate={property.fundingStartAt}
                  onCountdownComplete={handleActionOnCountdownComplete}
                  className="text-xs text-neutralBlack"
                />
              </div>
            )}
            {property.fundingStatus === 'reimbursed' && (
              <div className="pt-3 text-xs font-semibold text-emerald-700">
                {t('buyCard.reimbursed')}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
